import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { 
  Settings, Key, Sparkles, Brain, Banknote, Shield, Heart, Save, Eye, EyeOff, Check
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const AISettings = () => {
  const [config, setConfig] = useState({
    openai_api_key: "",
    recruitment_agent_enabled: true,
    payroll_agent_enabled: true,
    compliance_agent_enabled: true,
    engagement_agent_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [newKey, setNewKey] = useState("");

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/ai-config`, { headers, withCredentials: true });
      setConfig(response.data);
    } catch (error) {
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const updateData = {
        recruitment_agent_enabled: config.recruitment_agent_enabled,
        payroll_agent_enabled: config.payroll_agent_enabled,
        compliance_agent_enabled: config.compliance_agent_enabled,
        engagement_agent_enabled: config.engagement_agent_enabled
      };
      
      if (newKey) {
        updateData.openai_api_key = newKey;
      }
      
      await axios.put(`${API}/ai-config`, updateData, { headers, withCredentials: true });
      toast.success("Configuración guardada");
      setNewKey("");
      fetchConfig();
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const agents = [
    {
      id: "recruitment",
      name: "Agente de Reclutamiento",
      description: "Analiza CVs, genera descripciones de puesto, evalúa candidatos y detecta sesgos.",
      icon: Brain,
      color: "text-violet-600",
      bgColor: "bg-violet-100",
      enabled: config.recruitment_agent_enabled,
      toggle: () => setConfig(p => ({ ...p, recruitment_agent_enabled: !p.recruitment_agent_enabled }))
    },
    {
      id: "payroll",
      name: "Agente de Nómina",
      description: "Valida cálculos, verifica cumplimiento fiscal mexicano, detecta errores.",
      icon: Banknote,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      enabled: config.payroll_agent_enabled,
      toggle: () => setConfig(p => ({ ...p, payroll_agent_enabled: !p.payroll_agent_enabled }))
    },
    {
      id: "compliance",
      name: "Agente de Compliance",
      description: "Monitorea leyes laborales, alerta sobre cambios regulatorios, verifica políticas.",
      icon: Shield,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
      enabled: config.compliance_agent_enabled,
      toggle: () => setConfig(p => ({ ...p, compliance_agent_enabled: !p.compliance_agent_enabled }))
    },
    {
      id: "engagement",
      name: "Agente de Engagement",
      description: "Analiza clima laboral, detecta riesgo de rotación, sugiere mejoras de bienestar.",
      icon: Heart,
      color: "text-rose-600",
      bgColor: "bg-rose-100",
      enabled: config.engagement_agent_enabled,
      toggle: () => setConfig(p => ({ ...p, engagement_agent_enabled: !p.engagement_agent_enabled }))
    }
  ];

  if (loading) {
    return (
      <Layout title="Configuración de IA">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title="Configuración de IA" 
      subtitle="Administra tu API key y agentes de inteligencia artificial"
    >
      <div className="max-w-4xl space-y-6">
        {/* API Key */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Key className="w-5 h-5 text-violet-600" />
              API Key de OpenAI
            </CardTitle>
            <CardDescription>
              Conecta tu propia API key de OpenAI para habilitar los agentes de IA.
              Puedes obtener una en{" "}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
                platform.openai.com
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>API Key Actual</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 rounded-lg bg-slate-50 border border-slate-200 font-mono text-sm">
                  {config.openai_api_key ? (
                    <span className="text-emerald-600 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {config.openai_api_key}
                    </span>
                  ) : (
                    <span className="text-slate-400">No configurada</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Nueva API Key</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showKey ? "text" : "password"}
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder="sk-proj-..."
                    className="pr-10"
                    data-testid="api-key-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowKey(!showKey)}
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Tu API key se almacena de forma segura y nunca se comparte.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Agents */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Agentes de IA
            </CardTitle>
            <CardDescription>
              Activa o desactiva los agentes de IA según tus necesidades.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {agents.map((agent) => (
              <div 
                key={agent.id}
                className="flex items-start gap-4 p-4 rounded-lg bg-slate-50 border border-slate-100"
              >
                <div className={`w-12 h-12 rounded-xl ${agent.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <agent.icon className={`w-6 h-6 ${agent.color}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-slate-900">{agent.name}</h4>
                    <Switch
                      checked={agent.enabled}
                      onCheckedChange={agent.toggle}
                      data-testid={`toggle-${agent.id}`}
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{agent.description}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            className="bg-violet-600 hover:bg-violet-700"
            disabled={saving}
            data-testid="save-ai-config-btn"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Guardar Configuración
              </>
            )}
          </Button>
        </div>

        {/* Usage Info */}
        <Card className="border-violet-200 bg-violet-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-violet-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-violet-900">¿Cómo funcionan los agentes?</h4>
                <p className="text-sm text-violet-700 mt-1">
                  Los agentes de IA utilizan GPT-5.2 de OpenAI para procesar tus consultas.
                  Cada agente está especializado en un área de RH y puede ayudarte con tareas
                  como análisis de CVs, validación de nómina, cumplimiento legal y clima laboral.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AISettings;
